import React, { useState } from 'react';
import { InlineButtonLayout } from '../InlineButtonEditor';

interface Props {
  buttons: InlineButtonLayout;
  onNavigate?: (targetPageIndex: number) => void;
  onSwitchContent?: (altText: string, altButtons?: InlineButtonLayout) => void;
}

interface ConfirmState {
  url: string;
  confirmText: string;
}

const InlineButtonPreview: React.FC<Props> = ({ buttons, onNavigate, onSwitchContent }) => {
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);

  if (!buttons || buttons.length === 0) return null;

  const handleClick = (btn: InlineButtonLayout[0][0]) => {
    if (btn.type === 'navigate') {
      if (btn.targetPageIndex !== undefined && onNavigate) {
        onNavigate(btn.targetPageIndex);
      }
      return;
    }

    if (btn.type === 'url') {
      const url = (btn as any).url || (btn as any).value || '';
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    if (btn.type === 'url_confirm') {
      const url = (btn as any).url || (btn as any).value || '';
      const confirmText = (btn as any).confirm_text || '确定要打开此链接吗？';
      setConfirmState({ url, confirmText });
      return;
    }

    if (btn.type === 'callback_switch') {
      const altText = (btn as any).alt_text || '';
      const altButtons = (btn as any).alt_buttons as InlineButtonLayout | undefined;
      if (onSwitchContent) {
        onSwitchContent(altText, altButtons && altButtons.length > 0 ? altButtons : undefined);
      }
      return;
    }

    if (btn.type === 'web_app') {
      // 预览环境无法真正打开 Mini App，用新标签页模拟
      const url = (btn as any).url || (btn as any).value || '';
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    }
  };

  return (
    <div className="tg-buttons-container">
      {buttons.map((row, rowIdx) => (
        <div key={rowIdx} className="tg-button-row">
          {row.map((btn, btnIdx) => {
            const isNavigate = btn.type === 'navigate';
            const isUrl = btn.type === 'url' || btn.type === 'url_confirm';
            const isSwitch = btn.type === 'callback_switch';
            const isWebApp = btn.type === 'web_app';
            const classes = [
              'tg-button',
              isNavigate ? 'navigate' : '',
              isUrl ? 'url-ext' : '',
              isSwitch ? 'switch' : '',
              isWebApp ? 'web-app' : '',
            ].filter(Boolean).join(' ');

            return (
              <button
                key={btnIdx}
                type="button"
                className={classes}
                onClick={() => handleClick(btn)}
                title={
                  isNavigate
                    ? `跳转到页面 ${btn.targetPageIndex}`
                    : isSwitch
                    ? `切换内容: ${(btn as any).alt_text || ''}`
                    : isWebApp
                    ? `打开 Mini App: ${(btn as any).url || (btn as any).value || ''}`
                    : (btn as any).url || (btn as any).alt_text || btn.text
                }
              >
                {btn.text || `按钮${btnIdx + 1}`}
              </button>
            );
          })}
        </div>
      ))}

      {confirmState && (
        <div className="tg-confirm-overlay">
          <div className="tg-confirm-box">
            <p className="tg-confirm-text">{confirmState.confirmText}</p>
            <div className="tg-confirm-actions">
              <button
                type="button"
                className="tg-confirm-ok"
                onClick={() => {
                  if (confirmState.url) {
                    window.open(confirmState.url, '_blank', 'noopener,noreferrer');
                  }
                  setConfirmState(null);
                }}
              >
                确认跳转
              </button>
              <button
                type="button"
                className="tg-confirm-cancel"
                onClick={() => setConfirmState(null)}
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InlineButtonPreview;
